import type {
  NodeCategory,
  NodeColorOverride,
  NodeStatus,
  NodeTypeId,
  SemanticNodeData,
} from '@/domain/funnel/node-system';
import { NODE_CATEGORIES, NODE_STATUSES, NODE_TYPE_IDS } from '@/domain/funnel/node-system';
import type { MetricRole, NodeMetricKey } from '@/domain/metrics/contracts';

export type PropertyFieldKind =
  'text' | 'textarea' | 'url' | 'status' | 'color' | 'attachments' | 'readonly';
export type PropertyKey =
  | 'name'
  | 'description'
  | 'category'
  | 'icon'
  | 'colorOverride'
  | 'url'
  | 'responsible'
  | 'notes'
  | 'status'
  | 'attachments';
export interface PropertyFieldDefinition {
  key: PropertyKey;
  label: string;
  kind: PropertyFieldKind;
  maxLength?: number;
}
export interface NodeCapabilities {
  canConnectIn: boolean;
  canConnectOut: boolean;
  resizable?: boolean;
}
export interface NodeDefinition {
  type: NodeTypeId;
  label: string;
  description: string;
  category: NodeCategory;
  icon: string;
  keywords: readonly string[];
  rendererKey: 'semantic' | 'note' | 'group-frame' | 'condition';
  defaultData: SemanticNodeData;
  properties: readonly PropertyFieldDefinition[];
  capabilities: NodeCapabilities;
  /** SPEC-015 T031 — semantic terminal for diagnosis, independent from connection capability. */
  isTerminal: boolean;
  /** SPEC-008 §12/T009 — which metric fields the Properties panel shows for this type. */
  supportedMetrics: readonly NodeMetricKey[];
  /** SPEC-008 §58/T010/T011 — conservative funnel-aggregation role defaults; see T012 for instance overrides. */
  metricRoles: readonly MetricRole[];
}

const commonProperties: readonly PropertyFieldDefinition[] = [
  { key: 'name', label: 'Nome', kind: 'text', maxLength: 120 },
  { key: 'description', label: 'Descrição', kind: 'textarea', maxLength: 500 },
  { key: 'category', label: 'Categoria', kind: 'readonly' },
  { key: 'icon', label: 'Ícone', kind: 'readonly' },
  { key: 'colorOverride', label: 'Cor', kind: 'color' },
  { key: 'url', label: 'URL', kind: 'url' },
  { key: 'responsible', label: 'Responsável', kind: 'text', maxLength: 120 },
  { key: 'notes', label: 'Observações', kind: 'textarea', maxLength: 4000 },
  { key: 'status', label: 'Status', kind: 'status' },
  { key: 'attachments', label: 'Anexos', kind: 'attachments' },
];

const specs: ReadonlyArray<[NodeTypeId, string, NodeCategory, string, string, readonly string[]]> =
  [
    [
      'generic',
      'Etapa genérica',
      'documentation',
      'Box',
      'Uma etapa flexível do funil.',
      ['custom', 'etapa'],
    ],
    [
      'traffic-source',
      'Fonte de tráfego',
      'acquisition',
      'Megaphone',
      'Uma origem de tráfego.',
      ['ads', 'tráfego', 'google', 'meta'],
    ],
    [
      'social-network',
      'Rede social',
      'acquisition',
      'Share2',
      'Um canal social.',
      ['instagram', 'linkedin', 'tiktok', 'youtube'],
    ],
    ['site', 'Site', 'conversion', 'Globe2', 'Um site institucional.', ['website', 'página']],
    [
      'landing-page',
      'Página de destino',
      'conversion',
      'PanelsTopLeft',
      'Uma página de destino para conversão.',
      ['lp', 'lead'],
    ],
    [
      'form',
      'Formulário',
      'conversion',
      'ClipboardList',
      'Um formulário de captura de dados.',
      ['capture', 'formulário'],
    ],
    [
      'lead-magnet',
      'Isca digital',
      'conversion',
      'Magnet',
      'Um recurso para captura de leads.',
      ['ebook', 'material', 'isca'],
    ],
    [
      'whatsapp',
      'WhatsApp',
      'commercial',
      'MessageCircle',
      'Uma conversa no WhatsApp.',
      ['chat', 'mensagem'],
    ],
    ['email', 'E-mail', 'nutrition', 'Mail', 'Um ponto de contato por e-mail.', ['newsletter', 'nutrição']],
    ['call', 'Ligação', 'commercial', 'Phone', 'Uma ligação comercial.', ['ligação', 'telefone']],
    ['chatbot', 'Chatbot', 'nutrition', 'Bot', 'Uma conversa automatizada.', ['bot', 'chat']],
    ['crm', 'CRM', 'commercial', 'Database', 'Uma atividade de CRM.', ['pipeline', 'deal']],
    [
      'sdr-seller',
      'SDR / Vendedor',
      'commercial',
      'UserRound',
      'Uma ação comercial humana.',
      ['vendedor', 'sales'],
    ],
    [
      'meeting',
      'Reunião',
      'commercial',
      'CalendarDays',
      'Uma reunião ou agendamento.',
      ['reunião', 'agenda'],
    ],
    [
      'proposal',
      'Proposta',
      'commercial',
      'FileText',
      'Uma proposta comercial.',
      ['proposta', 'quote'],
    ],
    [
      'payment',
      'Pagamento',
      'revenue',
      'CreditCard',
      'Um pagamento ou checkout.',
      ['checkout', 'pagamento'],
    ],
    ['sale', 'Venda', 'revenue', 'BadgeDollarSign', 'Uma venda concluída.', ['venda', 'cliente']],
    [
      'post-sale',
      'Pós-venda',
      'retention',
      'Handshake',
      'Uma etapa de pós-venda ou integração.',
      ['onboarding', 'sucesso'],
    ],
    [
      'upsell',
      'Upsell',
      'retention',
      'TrendingUp',
      'Uma venda adicional.',
      ['upgrade', 'venda adicional'],
    ],
    [
      'renewal',
      'Renovação',
      'retention',
      'RefreshCw',
      'Uma etapa de renovação.',
      ['renovação', 'recorrência'],
    ],
    [
      'churn',
      'Churn',
      'retention',
      'UserMinus',
      'Um ponto de perda de cliente.',
      ['cancelamento', 'saída'],
    ],
    [
      'condition',
      'Condição',
      'logic',
      'GitBranch',
      'Uma decisão conceitual.',
      ['if', 'decisão', 'branch'],
    ],
    ['delay', 'Espera', 'logic', 'Clock3', 'Uma espera conceitual.', ['espera', 'tempo']],
    [
      'automation',
      'Automação',
      'automation',
      'Workflow',
      'Uma automação representacional.',
      ['workflow', 'automação'],
    ],
    [
      'webhook',
      'Webhook',
      'integration',
      'Webhook',
      'Um webhook representacional.',
      ['integração', 'hook'],
    ],
    [
      'api',
      'API',
      'integration',
      'Braces',
      'Uma integração de API representacional.',
      ['endpoint', 'integração'],
    ],
    [
      'ai-agent',
      'IA / Agente',
      'automation',
      'Sparkles',
      'Uma etapa conceitual assistida por IA.',
      ['ia', 'agent', 'ai'],
    ],
    [
      'note',
      'Nota',
      'documentation',
      'StickyNote',
      'Uma nota visual de documentação.',
      ['nota', 'texto'],
    ],
    [
      'group-frame',
      'Grupo / Quadro',
      'documentation',
      'Frame',
      'Um quadro visual de agrupamento.',
      ['grupo', 'frame', 'seção'],
    ],
  ];

function rendererFor(type: NodeTypeId): NodeDefinition['rendererKey'] {
  if (type === 'note' || type === 'group-frame' || type === 'condition') return type;
  return 'semantic';
}

function propertiesFor(type: NodeTypeId): readonly PropertyFieldDefinition[] {
  if (type === 'note')
    return commonProperties.filter((field) =>
      ['name', 'notes', 'colorOverride', 'attachments'].includes(field.key),
    );
  if (type === 'group-frame')
    return commonProperties.filter((field) =>
      ['name', 'description', 'colorOverride'].includes(field.key),
    );
  return commonProperties;
}

/**
 * SPEC-006 §13/EDGE-REQ default capabilities. Documentation-only node
 * types (`note`, `group-frame`) never accept connections — they annotate
 * the canvas, they are not part of the flow. `condition` gets multiple
 * named outputs (EDGE-REQ-034) instead of a single generic source handle.
 */
const DOCUMENTATION_TYPES: readonly NodeTypeId[] = ['note', 'group-frame'];
const DEFAULT_CONDITION_BRANCHES = ['Sim', 'Não'] as const;

/**
 * SPEC-008 §12/T009 — which node metrics apply per category. Deliberately
 * coarse-grained (by category, not per type) since applicability is a UI
 * "which fields to show" concern; funnel-aggregation ROLES (below) stay
 * per-type and explicit, since those carry real business meaning
 * (METRIC-REQ-020 — never infer a hidden role).
 */
const CATEGORY_METRICS: Record<NodeCategory, readonly NodeMetricKey[]> = {
  acquisition: ['entries', 'exits', 'conversion', 'cost', 'averageTime', 'goal'],
  conversion: ['entries', 'exits', 'conversion', 'cost', 'cpl', 'averageTime', 'goal'],
  nutrition: ['entries', 'exits', 'conversion', 'averageTime', 'goal'],
  commercial: ['entries', 'exits', 'conversion', 'cost', 'averageTime', 'goal'],
  revenue: [
    'entries',
    'exits',
    'conversion',
    'cost',
    'revenue',
    'cac',
    'averageTicket',
    'averageTime',
    'goal',
  ],
  retention: ['entries', 'exits', 'conversion', 'revenue', 'averageTime', 'goal'],
  automation: ['entries', 'exits', 'conversion', 'averageTime', 'goal'],
  integration: ['entries', 'exits', 'conversion', 'averageTime', 'goal'],
  logic: ['entries', 'exits', 'conversion', 'averageTime', 'goal'],
  documentation: [],
};

/**
 * SPEC-008 §56/§59/METRIC-REQ-019/T010/T011 — conservative, explicit
 * defaults anchored directly to the spec's own named examples. Every other
 * type gets no role: an ambiguous business case must be configured
 * explicitly per instance (T012), never inferred.
 */
const TYPE_METRIC_ROLES: Partial<Record<NodeTypeId, readonly MetricRole[]>> = {
  'traffic-source': ['media_cost', 'total_cost'],
  'social-network': ['media_cost', 'total_cost'],
  form: ['lead'],
  'lead-magnet': ['lead'],
  sale: ['new_customer', 'revenue'],
  payment: ['revenue', 'total_cost'],
  upsell: ['revenue'],
  renewal: ['revenue'],
  'post-sale': ['revenue'],
};

export const NODE_DEFINITIONS: readonly NodeDefinition[] = specs.map(
  ([type, label, category, icon, description, keywords]) => ({
    type,
    label,
    category,
    icon,
    description,
    keywords,
    rendererKey: rendererFor(type),
    properties: propertiesFor(type),
    capabilities: DOCUMENTATION_TYPES.includes(type)
      ? { canConnectIn: false, canConnectOut: false, resizable: type === 'group-frame' }
      : {
          // SPEC-006 §13 baseline: operational (non-documentation) types
          // default canConnectIn/Out = true. SPEC-010 §42-47 requires
          // outgoing edges from `sale`/`churn` (post-sale, upsell, renewal
          // flows), so no type-specific exception remains here.
          canConnectIn: type !== 'traffic-source',
          canConnectOut: true,
        },
    isTerminal: ['sale', 'churn', 'note', 'group-frame'].includes(type),
    supportedMetrics: CATEGORY_METRICS[category],
    metricRoles: TYPE_METRIC_ROLES[type] ?? [],
    defaultData: {
      name: label,
      category,
      icon,
      status: 'active' as NodeStatus,
      attachments: [],
      metrics: {},
      ...(type === 'condition' ? { branches: [...DEFAULT_CONDITION_BRANCHES] } : {}),
    },
  }),
);

const registry = new Map<NodeTypeId, NodeDefinition>(
  NODE_DEFINITIONS.map((definition) => [definition.type, definition]),
);
export function getNodeDefinition(type: string): NodeDefinition | undefined {
  return registry.get(type as NodeTypeId);
}
export function getAllNodeDefinitions(): readonly NodeDefinition[] {
  return NODE_DEFINITIONS;
}
export function getNodesByCategory(category: NodeCategory): readonly NodeDefinition[] {
  return NODE_DEFINITIONS.filter((definition) => definition.category === category);
}
export function isKnownNodeType(type: string): type is NodeTypeId {
  return registry.has(type as NodeTypeId);
}

export function validateRegistry(
  definitions: readonly NodeDefinition[] = NODE_DEFINITIONS,
): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const definition of definitions) {
    if (ids.has(definition.type)) errors.push(`Duplicate node type: ${definition.type}`);
    ids.add(definition.type);
    if (!NODE_CATEGORIES.includes(definition.category))
      errors.push(`Invalid category: ${definition.category}`);
    if (!definition.label.trim()) errors.push(`Missing label: ${definition.type}`);
    if (!definition.icon.trim()) errors.push(`Missing icon: ${definition.type}`);
    if (!definition.rendererKey) errors.push(`Missing renderer: ${definition.type}`);
    if (!definition.defaultData || definition.defaultData.category !== definition.category)
      errors.push(`Invalid defaults: ${definition.type}`);
  }
  for (const type of NODE_TYPE_IDS) if (!ids.has(type)) errors.push(`Missing node type: ${type}`);
  if (definitions.length !== NODE_TYPE_IDS.length)
    errors.push(`Expected ${NODE_TYPE_IDS.length} node types.`);
  return errors;
}

export const CATEGORY_LABELS: Record<NodeCategory, string> = {
  acquisition: 'Aquisição',
  conversion: 'Conversão',
  nutrition: 'Nutrição',
  commercial: 'Comercial',
  revenue: 'Receita',
  retention: 'Retenção',
  automation: 'Automação',
  integration: 'Integração',
  logic: 'Lógica',
  documentation: 'Documentação',
};
export const STATUS_LABELS: Record<NodeStatus, string> = {
  planned: 'Planejado',
  active: 'Ativo',
  attention: 'Atenção',
  problem: 'Problema',
  paused: 'Pausado',
};
export const COLOR_LABELS: Record<NodeColorOverride, string> = {
  neutral: 'Neutro',
  blue: 'Azul',
  green: 'Verde',
  amber: 'Âmbar',
  violet: 'Violeta',
};
export { NODE_CATEGORIES, NODE_STATUSES };
